import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getUserProfile } from '../lib/supabase';
import { trackEvent } from '../lib/posthog';
import SEO from '../components/SEO';
import UserTypeSelector, { type UserType } from '../components/UserTypeSelector';
import { redirectToStripeCheckout, getLTDParentsPriceId, getLTDTrailheadPriceId, getLTDCampfirePriceId } from '../lib/stripeCheckout';

export default function SignInPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [userTypes, setUserTypes] = useState<UserType[]>([]);
  const [userTypeError, setUserTypeError] = useState('');
  const supabase = useSupabaseClient();
  const navigate = useNavigate();
  const session = useSession();

  // Sync route with sign-up/sign-in mode
  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam === 'business') {
      setUserTypes(['camp-owner']);
      setIsSignUp(true); // Auto-switch to sign-up mode
      // Navigate to register if not already there
      if (location.pathname !== '/register') {
        navigate('/register?type=business', { replace: true });
      }
    } else if (typeParam === 'parent') {
      setUserTypes(['parent']);
      setIsSignUp(true);
      if (location.pathname !== '/register') {
        navigate('/register?type=parent', { replace: true });
      }
    } else if (location.pathname === '/register') {
      setIsSignUp(true);
    } else if (location.pathname === '/login') {
      setIsSignUp(false);
    }
  }, [location.pathname, searchParams, navigate]);

  // Redirect authenticated users away from login page
  useEffect(() => {
    if (session) {
      // Check for LTD checkout intent first (highest priority)
      const checkoutIntent = sessionStorage.getItem('ltd_checkout_intent');
      if (checkoutIntent === 'ltd-parents') {
        const priceId = getLTDParentsPriceId();
        if (priceId) {
          sessionStorage.removeItem('ltd_checkout_intent');
          redirectToStripeCheckout({
            priceId,
            planName: 'ltd-parents',
            userEmail: session.user.email,
          }).catch((error) => {
            console.error('Failed to initiate checkout:', error);
            toast.error('Failed to start checkout. Please try again.');
            navigate('/');
          });
          return; // Don't navigate, Stripe will redirect
        } else {
          console.error('LTD Parents price ID not configured');
          toast.error('Payment system not configured. Please contact support.');
          sessionStorage.removeItem('ltd_checkout_intent'); // Clear invalid intent
        }
      }
      if (checkoutIntent === 'ltd-trailhead' || checkoutIntent === 'ltd-campfire') {
        const priceId = checkoutIntent === 'ltd-trailhead'
          ? getLTDTrailheadPriceId()
          : getLTDCampfirePriceId();
        if (priceId) {
          sessionStorage.removeItem('ltd_checkout_intent');
          redirectToStripeCheckout({
            priceId,
            planName: checkoutIntent,
            userEmail: session.user.email,
          }).catch((error) => {
            console.error('Failed to initiate checkout:', error);
            toast.error('Failed to start checkout. Please try again.');
            navigate('/');
          });
          return;
        } else {
          toast.error('Payment system not configured. Please contact support.');
          sessionStorage.removeItem('ltd_checkout_intent');
        }
      }

      const redirectParam = searchParams.get('redirect');
      if (redirectParam) {
        // Check for post-confirmation redirect (set during sign-up)
        const postConfirmRedirect = sessionStorage.getItem('post_confirm_redirect');
        if (postConfirmRedirect) {
          sessionStorage.removeItem('post_confirm_redirect');
          navigate(postConfirmRedirect);
          return;
        }
        // Regular redirect
        navigate(redirectParam);
      } else {
        // Default redirect for authenticated users
        navigate('/');
      }
    }
  }, [session, navigate, searchParams]);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Track successful sign in
      trackEvent('user_signed_in', {
        method: 'email',
        email: email,
        timestamp: new Date().toISOString()
      });

      // Check for LTD checkout intent
      const planParam = searchParams.get('plan');
      const checkoutIntent = sessionStorage.getItem('ltd_checkout_intent');

      if (planParam === 'ltd-parents' || checkoutIntent === 'ltd-parents') {
        const priceId = getLTDParentsPriceId();
        if (priceId) {
          sessionStorage.removeItem('ltd_checkout_intent'); // Clear intent
          try {
            // Get session to get user email
            const { data: { session } } = await supabase.auth.getSession();
            await redirectToStripeCheckout({
              priceId,
              planName: 'ltd-parents',
              userEmail: session?.user?.email,
            });
            return; // Don't navigate, Stripe will redirect
          } catch (error) {
            console.error('Failed to initiate checkout:', error);
            toast.error('Failed to start checkout. Please try again.');
            // Fall through to default redirect
          }
        } else {
          console.error('LTD Parents price ID not configured');
          toast.error('Payment system not configured. Please contact support.');
        }
      }
      if (planParam === 'ltd-trailhead' || planParam === 'ltd-campfire' || checkoutIntent === 'ltd-trailhead' || checkoutIntent === 'ltd-campfire') {
        const plan = planParam === 'ltd-trailhead' || checkoutIntent === 'ltd-trailhead' ? 'ltd-trailhead' : 'ltd-campfire';
        const priceId = plan === 'ltd-trailhead' ? getLTDTrailheadPriceId() : getLTDCampfirePriceId();
        if (priceId) {
          sessionStorage.removeItem('ltd_checkout_intent');
          try {
            const { data: { session } } = await supabase.auth.getSession();
            await redirectToStripeCheckout({
              priceId,
              planName: plan,
              userEmail: session?.user?.email,
            });
            return;
          } catch (error) {
            console.error('Failed to initiate checkout:', error);
            toast.error('Failed to start checkout. Please try again.');
          }
        } else {
          toast.error('Payment system not configured. Please contact support.');
          sessionStorage.removeItem('ltd_checkout_intent');
        }
      }

      toast.success('Successfully signed in');
      
      // Check for redirect parameter
      const redirectParam = searchParams.get('redirect');
      if (redirectParam) {
        navigate(redirectParam);
      } else {
        navigate('/');
      }
    } catch (error: any) {
      // Don't log expected authentication errors to console (still show to user via toast)
      const isInvalidCredentials = error?.message?.includes('Invalid login credentials') || 
                                   error?.message?.includes('Invalid credentials') ||
                                   error?.code === 'invalid_credentials';
      
      if (!isInvalidCredentials) {
        console.error('Error signing in:', error);
      }
      
      // Track sign in error
      trackEvent('sign_in_error', {
        method: 'email',
        email: email,
        error_message: (error as any)?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
      
      toast.error(error.message || 'Failed to sign in');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    // For sign-up mode, validate user type selection first
    if (isSignUp && userTypes.length === 0) {
      setUserTypeError('Please select your account type before continuing');
      toast.error('Please select whether you\'re a parent or camp owner');
      return;
    }
    setUserTypeError('');

    try {
      // Store user types in sessionStorage for post-OAuth callback
      if (isSignUp) {
        sessionStorage.setItem('pending_user_types', JSON.stringify(userTypes));
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      // Track successful Google sign in
      trackEvent('user_signed_in', {
        method: 'google',
        email: email,
        user_types: isSignUp ? userTypes : undefined,
        timestamp: new Date().toISOString()
      });

    } catch (error: any) {
      // Don't log expected authentication errors to console (still show to user via toast)
      const isExpectedError = error?.message?.includes('Invalid login credentials') || 
                             error?.message?.includes('Invalid credentials') ||
                             error?.message?.includes('popup_closed_by_user') ||
                             error?.code === 'invalid_credentials';
      
      if (!isExpectedError) {
        console.error('Error signing in with Google:', error);
      }
      
      // Track Google sign in error
      trackEvent('sign_in_error', {
        method: 'google',
        email: email,
        error_message: (error as any)?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
      
      toast.error('Failed to sign in with Google');
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate user type selection
    if (userTypes.length === 0) {
      setUserTypeError('Please select at least one option');
      return;
    }
    setUserTypeError('');
    
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            user_types: userTypes, // Store in user metadata
            full_name: '' // Add full_name to avoid validation issues
          }
        }
      });

      if (error) {
        if (error.message.includes('already registered') || error.code === 'user_already_exists') {
          setIsSignUp(false);
          toast((t) => (
            <div className="flex flex-col">
              <span>An account with this email already exists.</span>
              <button
                onClick={() => {
                  setIsSignUp(false);
                  toast.dismiss(t.id);
                }}
                className="mt-2 text-sm text-primary hover:text-primary/80"
              >
                Sign in instead
              </button>
            </div>
          ), { duration: 5000 });
        } else {
          throw error;
        }
        return;
      }

      // User types are already stored in user_metadata during signup, no need for additional RPC call

      // Track successful sign up
      trackEvent('user_signed_up', {
        method: 'email',
        email: email,
        user_types: userTypes,
        timestamp: new Date().toISOString()
      });

      // Welcome email is automatically sent via database trigger (on_profile_created_send_welcome_email)
      // No need to call the Edge Function directly from the frontend

      toast.success('Account created! Welcome email sent.');
      
      // Check if there's a redirect URL from claim flow
      const redirectParam = searchParams.get('redirect');
      if (redirectParam) {
        // Store the redirect for after email confirmation
        sessionStorage.setItem('post_confirm_redirect', redirectParam);
      }
      
      setIsSignUp(false); // Switch back to sign in mode
    } catch (error: any) {
      // Don't log expected sign-up errors to console (still show to user via toast)
      const isExpectedError = error?.message?.includes('already registered') || 
                             error?.code === 'user_already_exists' ||
                             error?.message?.includes('User already registered');
      
      if (!isExpectedError) {
        console.error('Error signing up:', error);
      }
      
      // Track sign up error
      trackEvent('sign_up_error', {
        method: 'email',
        email: email,
        error_message: (error as any)?.message || 'Unknown error',
        error_code: (error as any)?.code || 'unknown',
        timestamp: new Date().toISOString()
      });
      
      if (error.message.includes('already registered') || error.code === 'user_already_exists') {
        setIsSignUp(false);
        toast.error('Account already exists. Please sign in instead.');
      } else {
        toast.error(error.message || 'Failed to create account');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login?reset=true`,
      });

      if (error) throw error;

      // Track password reset request
      trackEvent('password_reset_requested', {
        email: email,
        timestamp: new Date().toISOString()
      });

      toast.success('Password reset instructions sent to your email');
      setIsForgotPassword(false);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      
      // Track password reset error
      trackEvent('password_reset_error', {
        email: email,
        error_message: error.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
      
      toast.error(error.message || 'Failed to send reset instructions');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Sign In / Sign Up | CampWhiz"
        description="Sign in to your CampWhiz account or create a new one to save favorite camps, manage schedules, and plan your child's summer activities."
        keywords="sign in, sign up, login, register, campwhiz account, summer camp account"
        url="/login"
      />
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-32">
        <div className="max-w-md w-full space-y-6 bg-white p-6 rounded-lg shadow-md">
        <div>
          <h1 className="text-center text-3xl font-semibold text-gray-900">
            {isForgotPassword 
              ? 'Reset Password'
              : isSignUp 
                ? 'Get Started' 
                : 'Welcome Back'}
          </h1>
          <div className="mt-2 text-center space-y-2">
            <p className="text-sm text-gray-600">
              {isForgotPassword
                ? 'Enter your email to receive reset instructions'
                : isSignUp 
                  ? 'Find the perfect camp or grow your business' 
                  : 'Access your saved camps and schedules'}
            </p>
            {isSignUp && (
              <p className="text-xs text-gray-500">
                By signing up, you agree to our{' '}
                <Link to="/privacy" className="text-primary hover:text-primary/80">Privacy Policy</Link>
              </p>
            )}
          </div>
        </div>

        {/* User Type Selection - Show FIRST during sign up */}
        {isSignUp && !isForgotPassword && (
          <>
            <UserTypeSelector
              selectedTypes={userTypes}
              onChange={setUserTypes}
              error={userTypeError}
            />

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Choose your sign-up method</span>
              </div>
            </div>
          </>
        )}

        {!isForgotPassword && (
          <>
            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center px-4 py-3 border border-1 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border-gray-300 transition-colors"
            >
              <img
                className="h-5 w-5 mr-2"
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google logo"
              />
              Continue with Google
            </button>

            {!isSignUp && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with email</span>
                </div>
              </div>
            )}
          </>
        )}

        <form className="mt-6 space-y-4" onSubmit={isForgotPassword ? handleForgotPassword : isSignUp ? handleSignUp : handleEmailSignIn}>
          <div className="space-y-3">
            <div>
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-field appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {!isForgotPassword && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete={isSignUp ? 'new-password' : 'current-password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-field appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm"
                    placeholder="••••••••"
                    minLength={6}
                  />
                </div>
                {isSignUp && (
                  <p className="mt-1 text-sm text-gray-500">
                    Password must be at least 6 characters long
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Forgot password link - only show for sign in */}
          {!isForgotPassword && !isSignUp && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => {
                  setIsForgotPassword(true);
                  
                  // Track forgot password mode switch
                  trackEvent('auth_mode_switched', {
                    from_mode: 'signin',
                    to_mode: 'forgot_password',
                    timestamp: new Date().toISOString()
                  });
                }}
                className="text-sm font-medium text-primary hover:text-primary/80"
              >
                Forgot your password?
              </button>
            </div>
          )}

          {/* Back to sign in - for forgot password */}
          {isForgotPassword && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setIsForgotPassword(false);
                  
                  // Track back to sign in mode switch
                  trackEvent('auth_mode_switched', {
                    from_mode: 'forgot_password',
                    to_mode: 'signin',
                    timestamp: new Date().toISOString()
                  });
                }}
                className="text-sm font-medium text-primary hover:text-primary/80"
              >
                Back to sign in
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading 
              ? (isForgotPassword 
                ? 'Sending reset instructions...'
                : isSignUp 
                  ? 'Creating account...' 
                  : 'Signing in...') 
              : (isForgotPassword
                ? 'Send reset instructions'
                : isSignUp 
                  ? 'Create account' 
                  : 'Sign in')}
          </button>

          {/* Toggle between sign in / sign up - below button */}
          {!isForgotPassword && (
            <div className="text-center">
              {isSignUp ? (
                <Link
                  to="/login"
                  className="text-sm font-medium text-primary hover:text-primary/80"
                  onClick={() => {
                    setPassword(''); // Clear password when switching modes
                    setUserTypes([]); // Clear user types when switching
                    setUserTypeError(''); // Clear errors
                    
                    // Track mode switch
                    trackEvent('auth_mode_switched', {
                      from_mode: 'signup',
                      to_mode: 'signin',
                      timestamp: new Date().toISOString()
                    });
                  }}
                >
                  Already have an account? Sign in
                </Link>
              ) : (
                <Link
                  to="/register"
                  className="text-sm font-medium text-primary hover:text-primary/80"
                  onClick={() => {
                    setPassword(''); // Clear password when switching modes
                    setUserTypes([]); // Clear user types when switching
                    setUserTypeError(''); // Clear errors
                    
                    // Track mode switch
                    trackEvent('auth_mode_switched', {
                      from_mode: 'signin',
                      to_mode: 'signup',
                      timestamp: new Date().toISOString()
                    });
                  }}
                >
                  Need an account? Sign up
                </Link>
              )}
            </div>
          )}
        </form>
        </div>
      </div>
    </>
  );
}